import { HelpLink } from "@components/help/HelpLink";
import {
  formatThresholdFailure,
  type ThresholdFailure,
  ThresholdFlag,
} from "@components/shared/ThresholdFlag";
import { EVALUATION_ANCHORS } from "@lib/constants/scoringScales";
import { Save, Sparkles, X } from "lucide-react";
import { useState } from "react";

const MIN_EVIDENCE_LENGTH = 20;
const MIN_OVERRIDE_REASON_LENGTH = 10;

interface CapabilityDimension {
  id: string;
  continuityId: string;
  name: string;
  description: string | null;
  idealScore: number | null;
}

interface BehaviouralAnchor {
  id: string;
  evidenceText: string;
  sourceExcerpt: string | null;
  sourceArtefactType: string | null;
  sourceArtefactId: string | null;
  aiGenerated: boolean;
  createdAt: string;
}

interface ExistingScore {
  id?: string;
  capabilityDimension: { id: string; continuityId: string };
  score: number | null;
  evidence: string | null;
  notes: string | null;
  detailedNotes: string | null;
  behaviouralAnchors?: BehaviouralAnchor[];
}

interface AssessmentFormProps {
  dimensions: CapabilityDimension[];
  stage?: string;
  initial?: {
    overallSummary: string;
    strengths: string;
    concerns: string;
    culturalFitNotes: string;
    trajectoryNotes: string;
    riskNotes: string;
    competencyScores: ExistingScore[];
  };
  onSave: (data: {
    overallSummary: string;
    strengths: string;
    concerns: string;
    culturalFitNotes: string;
    trajectoryNotes: string;
    riskNotes: string;
    competencyScores: {
      capabilityDimensionId: string;
      score: number;
      evidence: string;
      notes: string;
      detailedNotes: string;
    }[];
  }) => void;
  onCancel: () => void;
  onSuggestEvidence?: (competencyScoreId: string) => void;
  suggestingEvidenceForId?: string | null;
  // Threshold UI props — present when an existing assessment version is being
  // edited. New (pre-save) versions don't have a server-side id yet, so these
  // controls are hidden until the assessor saves the first time.
  assessmentVersionId?: string | null;
  thresholdPassing?: boolean | null;
  thresholdOverriddenAt?: string | null;
  thresholdOverrideReason?: string | null;
  thresholdFailures?: ThresholdFailure[];
  onOverrideThreshold?: (versionId: string, reason: string) => void;
  onClearThresholdOverride?: (versionId: string) => void;
  saving: boolean;
}

const ANCHORS = EVALUATION_ANCHORS;

function anchorTierClass(score: number, selected: boolean): string {
  if (!selected) {
    return "border-input-border bg-card text-text-secondary hover:bg-card-hover";
  }
  if (score === 1) return "border-rose-600 bg-rose-50 text-rose-800";
  if (score === 2) return "border-amber-600 bg-amber-50 text-amber-800";
  if (score === 3) return "border-emerald-400 bg-emerald-50 text-emerald-800";
  return "border-emerald-600 bg-emerald-100 text-emerald-900";
}

interface ScoreRowState {
  id?: string;
  score: number | null;
  evidence: string;
  notes: string;
  detailedNotes: string;
  anchors: BehaviouralAnchor[];
}

export function AssessmentForm({
  dimensions,
  stage,
  initial,
  onSave,
  onCancel,
  onSuggestEvidence,
  suggestingEvidenceForId,
  assessmentVersionId,
  thresholdPassing,
  thresholdOverriddenAt,
  thresholdOverrideReason,
  thresholdFailures,
  onOverrideThreshold,
  onClearThresholdOverride,
  saving,
}: AssessmentFormProps) {
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [overallSummary, setOverallSummary] = useState(initial?.overallSummary ?? "");
  const [strengths, setStrengths] = useState(initial?.strengths ?? "");
  const [concerns, setConcerns] = useState(initial?.concerns ?? "");
  const [culturalFitNotes, setCulturalFitNotes] = useState(initial?.culturalFitNotes ?? "");
  const [trajectoryNotes, setTrajectoryNotes] = useState(initial?.trajectoryNotes ?? "");
  const [riskNotes, setRiskNotes] = useState(initial?.riskNotes ?? "");

  const initialScores: Record<string, ScoreRowState> = {};
  for (const dim of dimensions) {
    const existing = initial?.competencyScores.find(
      (s) => s.capabilityDimension.continuityId === dim.continuityId,
    );
    initialScores[dim.id] = {
      id: existing?.id,
      score: existing?.score ?? null,
      evidence: existing?.evidence ?? "",
      notes: existing?.notes ?? "",
      detailedNotes: existing?.detailedNotes ?? "",
      anchors: existing?.behaviouralAnchors ?? [],
    };
  }
  const [scores, setScores] = useState(initialScores);
  const [dismissedAnchorIds, setDismissedAnchorIds] = useState<Record<string, true>>({});

  const updateScore = (
    dimId: string,
    field: keyof ScoreRowState,
    value: string | number | null,
  ) => {
    setScores({ ...scores, [dimId]: { ...scores[dimId], [field]: value } });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      overallSummary,
      strengths,
      concerns,
      culturalFitNotes,
      trajectoryNotes,
      riskNotes,
      competencyScores: dimensions
        .filter((dim) => scores[dim.id]?.score != null)
        .map((dim) => ({
          capabilityDimensionId: dim.id,
          score: scores[dim.id].score as number,
          evidence: scores[dim.id]?.evidence ?? "",
          notes: scores[dim.id]?.notes ?? "",
          detailedNotes: scores[dim.id]?.detailedNotes ?? "",
        })),
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-text-secondary">Assessment</h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
          >
            <X size={12} /> Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-1 rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
          >
            <Save size={12} /> {saving ? "Saving..." : "Save Assessment"}
          </button>
        </div>
      </div>

      {/* Per-dimension evaluation (1–5 against required level) */}
      {dimensions.length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-medium text-text-secondary">Per-dimension evaluation</h4>
          <p className="mb-3 inline-flex flex-wrap items-center gap-1.5 text-xs text-text-faint">
            <span>
              Score each dimension 1–5 against its required level — not against absolute capability.
              The question is "given we need an 8, how well do they meet that bar?".
            </span>
            <HelpLink to="/help/scoring-scales" label="How the 1–10 and 1–5 scales relate" />
          </p>
          <div className="space-y-3">
            {dimensions.map((dim) => {
              const row = scores[dim.id];
              const disabled = dim.idealScore == null;
              const selected = row?.score ?? null;
              return (
                <div key={dim.id} className="rounded border border-border p-3">
                  <div className="mb-2">
                    <span className="text-sm font-medium text-text-primary">{dim.name}</span>
                    {dim.description && (
                      <span className="ml-2 text-xs text-text-faint">{dim.description}</span>
                    )}
                  </div>

                  {disabled ? (
                    <p className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
                      Set a required level on the blueprint before scoring this dimension.
                    </p>
                  ) : (
                    <p className="mb-2 text-xs text-text-secondary">
                      Required: <strong>{dim.idealScore}</strong> / 10 — score this candidate
                      against that bar.
                    </p>
                  )}

                  <div
                    role="radiogroup"
                    aria-label={`Evaluation for ${dim.name}`}
                    className="flex flex-wrap gap-1.5"
                  >
                    {ANCHORS.map((a) => {
                      const isSelected = selected === a.score;
                      return (
                        <button
                          key={a.score}
                          type="button"
                          role="radio"
                          aria-checked={isSelected}
                          aria-label={`${a.score} — ${a.label}`}
                          disabled={disabled}
                          onClick={() => updateScore(dim.id, "score", a.score)}
                          className={`rounded border px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${anchorTierClass(a.score, isSelected)}`}
                        >
                          <span className="font-bold">{a.score}</span>{" "}
                          <span className="font-normal">— {a.short}</span>
                        </button>
                      );
                    })}
                  </div>

                  {selected != null && (
                    <p className="mt-1.5 text-[11px] italic text-text-faint">
                      {ANCHORS[selected - 1].label}
                    </p>
                  )}

                  <input
                    type="text"
                    value={row?.evidence ?? ""}
                    onChange={(e) => updateScore(dim.id, "evidence", e.target.value)}
                    className="mt-2 w-full rounded border border-input-border bg-card px-2 py-1 text-xs text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                    placeholder="Evidence for this score..."
                    disabled={disabled}
                  />
                  {selected != null &&
                    (() => {
                      const evidenceLength = (row?.evidence ?? "").trim().length;
                      const visibleAnchors = (row?.anchors ?? []).filter(
                        (a) => !dismissedAnchorIds[a.id],
                      );
                      const evidenceOk =
                        evidenceLength >= MIN_EVIDENCE_LENGTH || visibleAnchors.length > 0;
                      return (
                        <div className="mt-1 flex items-center justify-between text-[11px]">
                          <span className={evidenceOk ? "text-text-faint" : "text-rose-700"}>
                            {evidenceOk
                              ? `${evidenceLength} characters`
                              : `Evidence: ${evidenceLength} / ${MIN_EVIDENCE_LENGTH} required (or attach an anchor)`}
                          </span>
                          {onSuggestEvidence &&
                            (() => {
                              const drafting = !!row?.id && suggestingEvidenceForId === row.id;
                              return (
                                <button
                                  type="button"
                                  onClick={() => row?.id && onSuggestEvidence(row.id)}
                                  disabled={!row?.id || drafting}
                                  title={
                                    row?.id
                                      ? "Draft 1–3 evidence statements from the candidate's CV and notes"
                                      : "Save the assessment first to enable AI-suggested evidence"
                                  }
                                  className="flex items-center gap-1 rounded border border-input-border px-2 py-0.5 text-text-secondary hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  <Sparkles size={10} />
                                  {drafting ? "Drafting…" : "Suggest from candidate data"}
                                </button>
                              );
                            })()}
                        </div>
                      );
                    })()}

                  {(row?.anchors ?? []).filter((a) => !dismissedAnchorIds[a.id]).length > 0 && (
                    <ul className="mt-2 space-y-1.5">
                      {(row?.anchors ?? [])
                        .filter((a) => !dismissedAnchorIds[a.id])
                        .map((anchor) => (
                          <li
                            key={anchor.id}
                            className="rounded border border-input-border bg-card-hover/50 p-2 text-[11px] text-text-secondary"
                          >
                            <div className="mb-1 flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1.5">
                                {anchor.aiGenerated && (
                                  <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-violet-800">
                                    AI
                                  </span>
                                )}
                                {anchor.sourceArtefactType && (
                                  <span className="text-[10px] text-text-faint">
                                    Source: {anchor.sourceArtefactType}
                                  </span>
                                )}
                              </div>
                              <div className="flex gap-1">
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateScore(dim.id, "evidence", anchor.evidenceText)
                                  }
                                  className="rounded px-1.5 py-0.5 text-[10px] text-accent hover:bg-card-hover"
                                >
                                  Use this
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setDismissedAnchorIds({
                                      ...dismissedAnchorIds,
                                      [anchor.id]: true,
                                    })
                                  }
                                  className="rounded px-1 py-0.5 text-[10px] text-text-muted hover:bg-card-hover"
                                  aria-label="Dismiss suggestion"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                            <p>{anchor.evidenceText}</p>
                            {anchor.sourceExcerpt && (
                              <p className="mt-1 italic text-text-faint">
                                "{anchor.sourceExcerpt}"
                              </p>
                            )}
                          </li>
                        ))}
                    </ul>
                  )}

                  {stage === "shortlist" && (
                    <textarea
                      value={row?.detailedNotes ?? ""}
                      onChange={(e) => updateScore(dim.id, "detailedNotes", e.target.value)}
                      rows={3}
                      className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1.5 text-xs text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                      placeholder="Detailed notes for shortlist report..."
                      disabled={disabled}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Threshold panel — visible once an assessment version exists */}
      {assessmentVersionId && (thresholdPassing != null || thresholdOverriddenAt) && (
        <div className="rounded border border-border bg-card p-3 text-xs">
          <div className="flex items-center gap-2">
            <span className="font-semibold uppercase tracking-wide text-text-muted">Threshold</span>
            <ThresholdFlag
              passing={thresholdPassing ?? null}
              overridden={!!thresholdOverriddenAt}
              overrideReason={thresholdOverrideReason}
              failures={thresholdFailures}
              size="sm"
            />
          </div>
          {(thresholdFailures ?? []).length > 0 && !thresholdOverriddenAt && (
            <ul className="mt-2 list-disc pl-5 text-text-secondary">
              {(thresholdFailures ?? []).map((f) => (
                <li key={`${f.type}-${f.dimensionId ?? ""}`}>{formatThresholdFailure(f)}</li>
              ))}
            </ul>
          )}
          {thresholdOverriddenAt && thresholdOverrideReason && (
            <p className="mt-2 text-text-secondary">
              <span className="font-medium">Override reason:</span> {thresholdOverrideReason}
            </p>
          )}
          <div className="mt-2 flex gap-2">
            {thresholdPassing === false && !thresholdOverriddenAt && onOverrideThreshold && (
              <button
                type="button"
                onClick={() => {
                  setOverrideReason("");
                  setOverrideModalOpen(true);
                }}
                className="rounded border border-amber-500 px-2 py-1 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/30"
              >
                Override threshold
              </button>
            )}
            {thresholdOverriddenAt && onClearThresholdOverride && (
              <button
                type="button"
                onClick={() => onClearThresholdOverride(assessmentVersionId)}
                className="rounded border border-input-border px-2 py-1 text-text-secondary hover:bg-card-hover"
              >
                Clear override
              </button>
            )}
          </div>
        </div>
      )}

      {overrideModalOpen && assessmentVersionId && onOverrideThreshold && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30">
          <div className="w-96 rounded bg-card p-4 shadow-lg">
            <h4 className="text-sm font-semibold text-text-primary">Override threshold</h4>
            <p className="mt-1 text-xs text-text-muted">
              Recorded with your name and timestamp; visible to anyone reading the assessment.
            </p>
            <textarea
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              rows={3}
              placeholder={`Why is this candidate being accepted despite the threshold failure? (≥${MIN_OVERRIDE_REASON_LENGTH} chars)`}
              className="mt-2 w-full rounded border border-input-border bg-card px-2 py-1.5 text-xs text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOverrideModalOpen(false)}
                className="rounded border border-input-border px-2 py-1 text-xs text-text-secondary hover:bg-card-hover"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={overrideReason.trim().length < MIN_OVERRIDE_REASON_LENGTH}
                onClick={() => {
                  onOverrideThreshold(assessmentVersionId, overrideReason.trim());
                  setOverrideModalOpen(false);
                }}
                className="rounded bg-amber-600 px-2 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                Override
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overall Fields */}
      <div>
        <label className="block text-xs font-medium text-text-secondary">Overall Summary</label>
        <textarea
          value={overallSummary}
          onChange={(e) => setOverallSummary(e.target.value)}
          rows={2}
          className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-text-secondary">Strengths</label>
          <textarea
            value={strengths}
            onChange={(e) => setStrengths(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">Concerns</label>
          <textarea
            value={concerns}
            onChange={(e) => setConcerns(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">Cultural Fit</label>
          <textarea
            value={culturalFitNotes}
            onChange={(e) => setCulturalFitNotes(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">Trajectory</label>
          <textarea
            value={trajectoryNotes}
            onChange={(e) => setTrajectoryNotes(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-text-secondary">Risk Notes</label>
        <textarea
          value={riskNotes}
          onChange={(e) => setRiskNotes(e.target.value)}
          rows={2}
          className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </div>
    </form>
  );
}
